<template>
  <div class="container-fluid py-4">

    <!-- En-tête -->
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-shield-check fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">Créances assurances</h2>
          <small class="text-muted">Ce que les assureurs doivent à la clinique, de l'envoi du bordereau au règlement.</small>
        </div>
      </div>
      <button v-if="peutGerer" class="btn btn-primary btn-clinique" @click="ouvrirBordereau()">
        <i class="bi bi-file-earmark-plus me-1"></i> Nouveau bordereau
      </button>
    </div>

    <!-- Synthèse globale -->
    <div class="row g-3 mb-4" v-if="synthese">
      <div class="col-6 col-lg-3">
        <div class="card card-clinique p-3 h-100">
          <small class="text-muted">À envoyer aux assureurs</small>
          <div class="fs-4 fw-bold text-primary">{{ fcfa(synthese.global.aEnvoyer) }}</div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="card card-clinique p-3 h-100">
          <small class="text-muted">Envoyé, en attente de paiement</small>
          <div class="fs-4 fw-bold text-warning">{{ fcfa(synthese.global.enAttente) }}</div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="card card-clinique p-3 h-100">
          <small class="text-muted">Encaissé des assureurs</small>
          <div class="fs-4 fw-bold text-success">{{ fcfa(synthese.global.encaisse) }}</div>
        </div>
      </div>
      <div class="col-6 col-lg-3">
        <div class="card card-clinique p-3 h-100">
          <small class="text-muted">Rejeté par les assureurs</small>
          <div class="fs-4 fw-bold text-danger">{{ fcfa(synthese.global.rejete) }}</div>
        </div>
      </div>
    </div>

    <!-- Ancienneté des sommes dues -->
    <div class="card card-clinique p-3 mb-4" v-if="synthese && totalDu > 0">
      <div class="d-flex justify-content-between mb-2">
        <strong>Ancienneté des sommes dues</strong>
        <small class="text-muted">Total dû : {{ fcfa(totalDu) }}</small>
      </div>
      <div class="progress anciennete" style="height: 26px">
        <div
          v-for="t in tranches"
          :key="t.cle"
          class="progress-bar"
          :class="t.classe"
          :style="{ width: (synthese.global.anciennete[t.cle] / totalDu * 100) + '%' }"
          :title="`${t.libelle} : ${fcfa(synthese.global.anciennete[t.cle])}`"
        >
          <span v-if="synthese.global.anciennete[t.cle] / totalDu > 0.12">{{ t.libelle }}</span>
        </div>
      </div>
      <div class="d-flex flex-wrap gap-3 mt-2 small">
        <span v-for="t in tranches" :key="t.cle">
          <span class="legende" :class="t.classe"></span>{{ t.libelle }} : {{ fcfa(synthese.global.anciennete[t.cle]) }}
        </span>
      </div>
    </div>

    <!-- Onglets -->
    <ul class="nav nav-tabs mb-3">
      <li class="nav-item"><a class="nav-link" :class="{ active: onglet === 'assureurs' }" href="#" @click.prevent="onglet = 'assureurs'">Par assureur</a></li>
      <li class="nav-item"><a class="nav-link" :class="{ active: onglet === 'creances' }" href="#" @click.prevent="onglet = 'creances'">Créances</a></li>
      <li class="nav-item"><a class="nav-link" :class="{ active: onglet === 'bordereaux' }" href="#" @click.prevent="onglet = 'bordereaux'">Bordereaux</a></li>
    </ul>

    <div v-if="isLoading" class="text-center my-5"><div class="spinner-border text-primary"></div></div>

    <!-- Par assureur -->
    <div v-else-if="onglet === 'assureurs'" class="card shadow-sm card-clinique">
      <div class="table-responsive">
        <table class="table align-middle mb-0 table-hover modern-table-clinique">
          <thead>
            <tr>
              <th>Assureur</th>
              <th class="text-end">À envoyer</th>
              <th class="text-end">En attente</th>
              <th class="text-end">Encaissé</th>
              <th class="text-end">Rejeté</th>
              <th class="text-end">Plus ancienne</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in synthese?.assureurs || []" :key="a.assurance.id">
              <td class="fw-semibold">{{ a.assurance.nom }}</td>
              <td class="text-end">{{ fcfa(a.aEnvoyer) }}</td>
              <td class="text-end">{{ fcfa(a.enAttente) }}</td>
              <td class="text-end text-success">{{ fcfa(a.encaisse) }}</td>
              <td class="text-end text-danger">{{ fcfa(a.rejete) }}</td>
              <td class="text-end" :class="a.plusAncienneJours > 60 ? 'text-danger fw-semibold' : ''">
                {{ a.aEnvoyer + a.enAttente > 0 ? a.plusAncienneJours + ' j' : '—' }}
              </td>
              <td class="text-end">
                <button v-if="peutGerer && a.aEnvoyer > 0" class="btn btn-sm btn-outline-primary" @click="ouvrirBordereau(a.assurance.id)">
                  Préparer le bordereau
                </button>
              </td>
            </tr>
            <tr v-if="!synthese?.assureurs?.length">
              <td colspan="7" class="text-center text-muted py-4">
                Aucune créance pour le moment. Elles apparaissent automatiquement à chaque encaissement avec assurance.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Créances -->
    <div v-else-if="onglet === 'creances'">
      <div class="card card-clinique p-3 mb-3">
        <div class="row g-2 align-items-end">
          <div class="col-md-3">
            <label class="form-label small">Rechercher</label>
            <input v-model="lc.recherche" type="search" class="form-control form-control-sm" placeholder="Patient, n° d'assuré, référence…" @input="chercher('creances')">
          </div>
          <div class="col-md-2">
            <label class="form-label small">Assureur</label>
            <select v-model="filtres.assurance" class="form-select form-select-sm">
              <option value="">Tous</option>
              <option v-for="a in assurances" :key="a.id" :value="a.id">{{ a.nom }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small">Statut</label>
            <select v-model="filtres.statut" class="form-select form-select-sm">
              <option value="">Tous</option>
              <option v-for="(l, s) in statuts" :key="s" :value="s">{{ l }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small">Du</label>
            <input v-model="filtres.du" type="date" class="form-control form-control-sm">
          </div>
          <div class="col-md-2">
            <label class="form-label small">Au</label>
            <input v-model="filtres.au" type="date" class="form-control form-control-sm">
          </div>
          <div class="col-md-1 text-md-end small text-muted">
            Reste dû<br><strong class="text-dark">{{ fcfa(lc.resteDu) }}</strong>
          </div>
        </div>
      </div>
      <div class="card shadow-sm card-clinique">
        <div class="table-responsive" :class="{ 'opacity-50': lc.chargement }">
          <table class="table table-sm align-middle mb-0 table-hover modern-table-clinique">
            <thead>
              <tr>
                <th>Date</th><th>Assureur</th><th>Patient</th><th>N° assuré</th><th>Origine</th>
                <th class="text-end">Montant</th><th class="text-end">Reste</th><th>Statut</th><th>Bordereau</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in creances" :key="c.id">
                <td class="text-nowrap">{{ formatDate(c.dateActe) }}</td>
                <td>{{ c.assurance?.nom }}</td>
                <td>
                  <span v-if="c.patient">{{ c.patient.nom }} {{ c.patient.prenom }}</span>
                  <div class="small text-muted">{{ c.patient?.numeroDossier }}</div>
                </td>
                <td>{{ c.numeroAssure }}</td>
                <td class="small">{{ libelleOrigine(c.origine) }}<div class="text-muted">{{ c.reference }}</div></td>
                <td class="text-end">{{ fcfa(c.montant) }}</td>
                <td class="text-end">{{ fcfa(c.reste) }}</td>
                <td>
                  <span class="badge" :class="badge(c.statut)">{{ statuts[c.statut] || c.statut }}</span>
                  <div v-if="c.motifRejet" class="small text-danger">{{ c.motifRejet }}</div>
                </td>
                <td>
                  <router-link v-if="c.bordereau" :to="{ name: 'bordereau_assurance', params: { id: c.bordereau.id } }">
                    {{ c.bordereau.numero }}
                  </router-link>
                </td>
                <td>
                  <button
                    v-if="peutGerer && ['ENVOYEE', 'PARTIELLEMENT_REGLEE', 'A_ENVOYER'].includes(c.statut)"
                    class="btn btn-sm btn-outline-danger"
                    title="Enregistrer un rejet de l'assureur"
                    @click="rejeter(c)"
                  >Rejet</button>
                </td>
              </tr>
              <tr v-if="!lc.chargement && creances.length === 0">
                <td colspan="10" class="text-center text-muted py-4">Aucune créance pour ces critères.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="px-3 pb-3">
          <Pagination v-model:page="lc.page" v-model:par-page="lc.parPage" :total="lc.total" />
        </div>
      </div>
    </div>

    <!-- Bordereaux -->
    <div v-else class="card shadow-sm card-clinique">
      <div class="d-flex flex-wrap gap-2 p-3 pb-0">
        <input v-model="lb.recherche" type="search" class="form-control form-control-sm" style="max-width: 260px" placeholder="N° de bordereau, assureur…" @input="chercher('bordereaux')">
        <select v-model="lb.statut" class="form-select form-select-sm" style="width: auto">
          <option value="">Tous les statuts</option>
          <option v-for="(l, code) in statutsBordereau" :key="code" :value="code">{{ l }}</option>
        </select>
      </div>
      <div class="table-responsive" :class="{ 'opacity-50': lb.chargement }">
        <table class="table align-middle mb-0 table-hover modern-table-clinique">
          <thead>
            <tr>
              <th>Numéro</th><th>Assureur</th><th>Période</th><th>Actes</th>
              <th class="text-end">Total</th><th class="text-end">Réglé</th><th>Statut</th><th>Envoyé le</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="b in bordereaux"
              :key="b.id"
              class="clickable"
              @click="$router.push({ name: 'bordereau_assurance', params: { id: b.id } })"
            >
              <td class="fw-semibold">{{ b.numero }}</td>
              <td>{{ b.assurance?.nom }}</td>
              <td class="small">{{ formatDate(b.periodeDebut) }} → {{ formatDate(b.periodeFin) }}</td>
              <td>{{ b.nombreCreances }}</td>
              <td class="text-end">{{ fcfa(b.montantTotal) }}</td>
              <td class="text-end">{{ fcfa(b.montantRegle) }}</td>
              <td><span class="badge" :class="badgeBordereau(b.statut)">{{ statutsBordereau[b.statut] }}</span></td>
              <td class="small">{{ b.dateEnvoi ? formatDate(b.dateEnvoi) : '—' }}</td>
            </tr>
            <tr v-if="!lb.chargement && bordereaux.length === 0">
              <td colspan="8" class="text-center text-muted py-4">{{ lb.recherche || lb.statut ? 'Aucun bordereau ne correspond à la recherche.' : 'Aucun bordereau.' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="lb.page" v-model:par-page="lb.parPage" :total="lb.total" />
      </div>
    </div>

    <!-- Fenêtre nouveau bordereau -->
    <div v-if="nouveau" class="modal-backdrop-custom" @click.self="nouveau = null">
      <div class="modal-card card shadow">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h5 class="mb-0">Nouveau bordereau</h5>
          <button class="btn-close" @click="nouveau = null"></button>
        </div>
        <div class="card-body">
          <p class="small text-muted">
            Le bordereau regroupe toutes les créances « à envoyer » de l'assureur sur la période.
            Vous pourrez l'imprimer, puis le marquer comme envoyé.
          </p>
          <div class="mb-3">
            <label class="form-label">Assureur</label>
            <select v-model="nouveau.assurance" class="form-select">
              <option v-for="a in assurances" :key="a.id" :value="a.id">{{ a.nom }}</option>
            </select>
          </div>
          <div class="row g-2">
            <div class="col">
              <label class="form-label">Du</label>
              <input v-model="nouveau.du" type="date" class="form-control">
            </div>
            <div class="col">
              <label class="form-label">Au</label>
              <input v-model="nouveau.au" type="date" class="form-control">
            </div>
          </div>
          <div v-if="erreur" class="alert alert-danger mt-3 mb-0 py-2">{{ erreur }}</div>
        </div>
        <div class="card-footer text-end">
          <button class="btn btn-secondary me-2" @click="nouveau = null">Annuler</button>
          <button class="btn btn-primary" :disabled="!nouveau.assurance || enCours" @click="creerBordereau">Créer le bordereau</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api'
import dayjs from 'dayjs'
import Pagination from '@/components/commun/Pagination.vue'
import { demanderMotif } from '@/components/commun/dialogue'

export default {
  name: 'CreancesAssurance',
  components: { Pagination },
  data() {
    return {
      onglet: 'assureurs',
      synthese: null,
      creances: [],
      bordereaux: [],
      assurances: [],
      filtres: { assurance: '', statut: '', du: '', au: '' },
      // Pagination serveur des deux listes (créances, bordereaux)
      lc: { page: 1, parPage: 25, total: 0, recherche: '', chargement: false, resteDu: 0, charge: false, numero: 0 },
      lb: { page: 1, parPage: 25, total: 0, recherche: '', statut: '', chargement: false, numero: 0 },
      minuterie: null,
      nouveau: null,
      erreur: '',
      enCours: false,
      isLoading: false,
      statuts: {
        A_ENVOYER: 'À envoyer',
        ENVOYEE: 'Envoyée',
        PARTIELLEMENT_REGLEE: 'Partiellement réglée',
        REGLEE: 'Réglée',
        REJETEE: 'Rejetée',
        ANNULEE: 'Annulée'
      },
      statutsBordereau: {
        BROUILLON: 'Brouillon',
        ENVOYE: 'Envoyé',
        PARTIELLEMENT_REGLE: 'Partiellement réglé',
        REGLE: 'Réglé'
      },
      tranches: [
        { cle: '0_30', libelle: '0-30 j', classe: 'bg-success' },
        { cle: '31_60', libelle: '31-60 j', classe: 'bg-info' },
        { cle: '61_90', libelle: '61-90 j', classe: 'bg-warning' },
        { cle: 'plus_90', libelle: '+90 j', classe: 'bg-danger' }
      ]
    }
  },
  computed: {
    peutGerer() {
      return this.$store.getters.hasPermission('ASSURANCE_CREANCES_GERER')
    },
    totalDu() {
      const a = this.synthese?.global?.anciennete || {}
      return Object.values(a).reduce((s, v) => s + v, 0)
    }
  },
  watch: {
    onglet(o) {
      if (o === 'creances' && !this.lc.charge) this.chargerCreances()
      if (o === 'bordereaux') this.chargerBordereaux()
    },
    filtres: { deep: true, handler() { this.premierePage('creances') } },
    'lc.page'() { this.chargerCreances() },
    'lc.parPage'() { this.premierePage('creances') },
    'lb.page'() { this.chargerBordereaux() },
    'lb.parPage'() { this.premierePage('bordereaux') },
    'lb.statut'() { this.premierePage('bordereaux') },
  },
  beforeUnmount() {
    clearTimeout(this.minuterie)
  },
  async created() {
    this.isLoading = true
    await Promise.all([this.chargerSynthese(), this.chargerAssurances()])
    this.isLoading = false
  },
  methods: {
    async chargerSynthese() {
      try {
        this.synthese = (await api.getItems('creances-assurance/synthese')).data
      } catch (e) {
        notifier(messageErreur(e, 'Impossible de charger la synthèse.'), 'danger')
      }
    },
    async chargerAssurances() {
      try {
        const res = await api.getItems('assurances', { itemsperpage: 100 })
        this.assurances = res.data?.data || res.data?.['hydra:member'] || []
      } catch (e) {
        this.assurances = []
      }
    },
    async chargerCreances() {
      const l = this.lc
      const numero = ++l.numero
      l.chargement = true
      try {
        const res = (await api.liste('creances-assurance', { ...this.filtres, search: l.recherche.trim(), page: l.page, itemsperpage: l.parPage })).data
        if (numero !== l.numero) return
        this.creances = res.data || []
        l.total = Number(res.meta?.total_records ?? this.creances.length)
        l.resteDu = res.total || 0
        l.charge = true
        if (!this.creances.length && l.page > 1 && l.total > 0) l.page = Math.ceil(l.total / l.parPage)
      } catch (e) {
        if (numero === l.numero) notifier(messageErreur(e, 'Impossible de charger les créances.'), 'danger')
      } finally {
        if (numero === l.numero) l.chargement = false
      }
    },
    async chargerBordereaux() {
      const l = this.lb
      const numero = ++l.numero
      l.chargement = true
      try {
        const res = (await api.liste('bordereaux-assurance', { statut: l.statut, search: l.recherche.trim(), page: l.page, itemsperpage: l.parPage })).data
        if (numero !== l.numero) return
        this.bordereaux = res.data || []
        l.total = Number(res.meta?.total_records ?? this.bordereaux.length)
        if (!this.bordereaux.length && l.page > 1 && l.total > 0) l.page = Math.ceil(l.total / l.parPage)
      } catch (e) {
        if (numero === l.numero) notifier(messageErreur(e, 'Impossible de charger les bordereaux.'), 'danger')
      } finally {
        if (numero === l.numero) l.chargement = false
      }
    },
    /** Revient à la page 1 de la liste et recharge. */
    premierePage(liste) {
      const l = liste === 'creances' ? this.lc : this.lb
      if (l.page !== 1) l.page = 1
      else if (liste === 'creances') this.chargerCreances()
      else this.chargerBordereaux()
    },
    chercher(liste) {
      clearTimeout(this.minuterie)
      this.minuterie = setTimeout(() => this.premierePage(liste), 350)
    },
    ouvrirBordereau(assuranceId = null) {
      this.erreur = ''
      const moisPrecedent = dayjs().subtract(1, 'month')
      this.nouveau = {
        assurance: assuranceId || this.assurances[0]?.id || null,
        du: moisPrecedent.startOf('month').format('YYYY-MM-DD'),
        au: dayjs().format('YYYY-MM-DD')
      }
    },
    async creerBordereau() {
      this.erreur = ''
      this.enCours = true
      try {
        const res = await api.post('bordereaux-assurance', this.nouveau)
        this.nouveau = null
        this.$router.push({ name: 'bordereau_assurance', params: { id: res.data.id } })
      } catch (e) {
        this.erreur = messageErreur(e, 'Le bordereau n’a pas pu être créé.')
      } finally {
        this.enCours = false
      }
    },
    async rejeter(c) {
      const r = await demanderMotif({
        titre: 'Rejet de l’assureur',
        message: `Enregistrer le rejet de cette créance (${this.fcfa(c.montant)}) par ${c.assurance?.nom || 'l’assureur'} ?`,
        libelle: 'Motif du rejet',
        libelleValider: 'Enregistrer le rejet',
      })
      if (!r) return
      try {
        await api.post(`creances-assurance/${c.id}/rejeter`, { motif: r.motif })
        notifier('Rejet enregistré.', 'success')
        await Promise.all([this.chargerCreances(), this.chargerSynthese()])
      } catch (e) {
        notifier(messageErreur(e, 'Le rejet n’a pas pu être enregistré.'), 'danger')
      }
    },
    fcfa(v) {
      return Number(v || 0).toLocaleString('fr-FR') + ' FCFA'
    },
    formatDate(d) {
      return d ? dayjs(d).format('DD/MM/YYYY') : ''
    },
    libelleOrigine(o) {
      return { CONSULTATION: 'Consultation', FACTURE: 'Services', HOSPITALISATION: 'Hospitalisation', VENTE: 'Pharmacie', AUTRE: 'Autre' }[o] || o
    },
    badge(s) {
      return {
        A_ENVOYER: 'bg-primary', ENVOYEE: 'bg-warning text-dark', PARTIELLEMENT_REGLEE: 'bg-info text-dark',
        REGLEE: 'bg-success', REJETEE: 'bg-danger', ANNULEE: 'bg-secondary'
      }[s] || 'bg-light text-dark'
    },
    badgeBordereau(s) {
      return { BROUILLON: 'bg-secondary', ENVOYE: 'bg-warning text-dark', PARTIELLEMENT_REGLE: 'bg-info text-dark', REGLE: 'bg-success' }[s]
    }
  }
}
</script>

<style scoped>
.header-clinique { padding: 15px 20px; background-color: #e9f5ff; border-radius: 12px; }
.card-clinique { background: #f8fafd; border-radius: 12px; border: none; }
.btn-clinique { background: linear-gradient(90deg, #0d6efd, #6610f2); border: none; color: #fff; border-radius: 10px; }
.modern-table-clinique thead { background: linear-gradient(90deg, #8fd3f4, #0d6efd); color: #fff; }
.clickable { cursor: pointer; }
.legende { display: inline-block; width: 10px; height: 10px; border-radius: 2px; margin-right: 4px; }
.anciennete .progress-bar { font-size: 11px; }
.modal-backdrop-custom { position: fixed; inset: 0; background: rgba(0, 0, 0, .45); z-index: 1050; display: flex; align-items: flex-start; justify-content: center; padding: 60px 16px; }
.modal-card { width: 100%; max-width: 480px; border-radius: 12px; }
</style>
